import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Clock,
  ArrowLeft,
  Share2,
  CalendarDays,
  ChevronLeft,
  Star,
} from 'lucide-react';
import { fetchPublicReaderProfile } from '@/services/readerApi';
import { LoginModal } from '@/components/LoginModal';
import { InviteReaderDialog } from '@/components/readers/InviteReaderDialog';
import { ReaderAvatar } from '@/components/brand';
import type { Reader } from '@/types/reader';
import { useToast } from '@/hooks/use-toast';

const STATIC_ROOT = 'https://casting.heyjoe.io/api/s3/';

const ReaderProfilePage: React.FC = () => {
  const { readerId } = useParams<{ readerId: string }>();
  const { toast } = useToast();
  const [reader, setReader] = useState<Reader | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const isAuthenticated = !!(localStorage.getItem('authToken') || localStorage.getItem('token'));

  useEffect(() => {
    if (!readerId) return;
    setLoading(true);
    fetchPublicReaderProfile(readerId)
      .then(setReader)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [readerId]);

  // Referral funnel hand-off: checkout success lands here with ?book=1 so the
  // new subscriber goes straight into the booking dialog.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (!loading && reader && isAuthenticated && searchParams.get('book') === '1') {
      setShowInviteDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, reader]);

  const handleShare = async () => {
    const url = window.location.href;
    const name = reader ? `${reader.first_name} ${reader.last_name}` : 'Reader';
    const text = `Check out ${name} on Hey Joe — professional reader & scene partner`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `${name} — Hey Joe Reader`, text, url });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied to clipboard' });
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--hj-bg)' }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--hj-ink-4)' }} />
      </div>
    );
  }

  if (error || !reader) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
      >
        <div className="text-center" style={{ maxWidth: 420 }}>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 32,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            Reader not found
          </h1>
          <p
            style={{
              marginTop: 10,
              marginBottom: 22,
              color: 'var(--hj-ink-3)',
              fontSize: 14,
            }}
          >
            This profile may no longer be available.
          </p>
          <Link to="/readers">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Browse readers
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const profile = reader.freelancer_profile;
  const avatarUrl = reader.logo ? `${STATIC_ROOT}${reader.logo}` : null;
  const name = `${reader.first_name} ${reader.last_name}`.trim();
  const hasRates =
    profile?.reader_rate != null ||
    profile?.reader_director_rate != null ||
    profile?.reader_coach_rate != null;

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap"
        style={{ padding: '20px 0 10px' }}
      >
        <Link
          to="/readers"
          className="inline-flex items-center gap-1.5"
          style={{
            color: 'var(--hj-ink-3)',
            textDecoration: 'none',
            fontSize: 13,
          }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          All readers
        </Link>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex items-center gap-1.5"
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            background: 'transparent',
            border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink-2)',
            fontSize: 13,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          <Share2 size={14} strokeWidth={1.8} /> Share
        </button>
      </div>

      {/* Hero */}
      <section
        className="grid items-stretch gap-6 md:gap-10"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr)',
          margin: '4px 0 28px',
          padding: '26px 28px 28px',
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <style>{`
          @media (min-width: 768px) {
            .hj-profile-hero { grid-template-columns: auto minmax(0, 1fr) !important; }
          }
        `}</style>
        <div
          className="hj-profile-hero grid items-center gap-6 md:gap-10"
          style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
        >
          {/* Avatar */}
          <div className="flex justify-center md:justify-start flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={name}
                className="hj-headshot object-cover"
                style={{
                  width: 160,
                  height: 160,
                  border: '3px solid #fff',
                  boxShadow: '0 12px 32px oklch(0 0 0 / 0.18)',
                }}
              />
            ) : (
              <ReaderAvatar name={name || 'Reader'} size={160} />
            )}
          </div>
          {/* Identity */}
          <div className="text-center md:text-left min-w-0">
            <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>
              Reader Marketplace
            </div>
            <h1
              className="font-hj-display m-0"
              style={{
                fontSize: 'clamp(36px, 5vw, 64px)',
                lineHeight: 1,
                color: 'var(--hj-beige-ink)',
                letterSpacing: '-0.01em',
              }}
            >
              {name}
            </h1>
            {profile?.timezone && (
              <div
                className="inline-flex items-center gap-1.5 justify-center md:justify-start"
                style={{ marginTop: 14, color: 'var(--hj-beige-ink-2)', fontSize: 14 }}
              >
                <Clock size={14} /> {profile.timezone}
              </div>
            )}
            {profile?.reader_categories && profile.reader_categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 justify-center md:justify-start" style={{ marginTop: 14 }}>
                {profile.reader_categories.map((cat) => (
                  <span
                    key={cat}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: '#fff',
                      border: '1px solid var(--hj-beige-line)',
                      fontSize: 12,
                      color: 'var(--hj-beige-ink)',
                    }}
                  >
                    {cat}
                  </span>
                ))}
              </div>
            )}
            <div className="flex justify-center md:justify-start" style={{ marginTop: 22 }}>
              <button
                type="button"
                onClick={() => (isAuthenticated ? setShowInviteDialog(true) : setShowLogin(true))}
                className="inline-flex items-center gap-2"
                style={{
                  padding: '12px 22px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  minHeight: 48,
                }}
              >
                <CalendarDays size={16} strokeWidth={2} />
                Book a session
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content blocks */}
      <div className="flex flex-col" style={{ gap: 22, padding: '32px 0' }}>
        {/* Rates */}
        {hasRates && (
          <section>
            <div className="mono-label" style={{ marginBottom: 12 }}>
              <Star
                size={11}
                strokeWidth={2}
                style={{ display: 'inline', marginRight: 6, color: 'var(--hj-yellow-ink)' }}
              />
              Rates · per 15 min
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {profile?.reader_rate != null && (
                <RateCard label="Reader" rate={profile.reader_rate} />
              )}
              {profile?.reader_director_rate != null && (
                <RateCard label="Session Director" rate={profile.reader_director_rate} />
              )}
              {profile?.reader_coach_rate != null && (
                <RateCard label="Coach" rate={profile.reader_coach_rate} />
              )}
            </div>
            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--hj-ink)' }}>
              Hey Joe Plus members pay the listed rate. Non-members add a flat $5 booking fee.{' '}
              <a href="/plans?c=reader-booking" style={{ fontWeight: 600, color: 'var(--hj-yellow-ink)', textDecoration: 'none' }}>
                Join Plus to skip it →
              </a>
            </p>
          </section>
        )}

        {/* A reader writes ONE bio. This used to render it twice, as "About" and
            again as "General experience", because the serialiser fed the same
            string to two legacy fields. Full-contrast ink: this is the main thing
            a person reads before deciding to book someone. */}
        {profile?.reader_experience && (
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>About</div>
            <p
              className="whitespace-pre-line m-0"
              style={{ color: 'var(--hj-ink)', fontSize: 15, lineHeight: 1.6 }}
            >
              {profile.reader_experience}
            </p>
          </section>
        )}

        {/* Availability */}
        {profile?.reader_availability_notes && (
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>Availability</div>
            <p
              className="m-0"
              style={{
                color: 'var(--hj-ink-2)',
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {profile.reader_availability_notes}
            </p>
          </section>
        )}

        {/* Photos */}
        {reader.photos && reader.photos.length > 0 && (
          <section>
            <div className="mono-label" style={{ marginBottom: 12 }}>Photos</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {reader.photos.map((photo, i) => (
                <img
                  key={i}
                  src={`${STATIC_ROOT}${photo}`}
                  alt={`${name} photo ${i + 1}`}
                  className="w-full object-cover"
                  style={{
                    aspectRatio: '1 / 1',
                    borderRadius: 'var(--hj-r-sm)',
                    border: '1px solid var(--hj-hairline-soft)',
                  }}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Bottom CTA */}
      <div
        className="text-center"
        style={{
          padding: '40px 24px',
          marginTop: 16,
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-surface-1)',
          border: '1px solid var(--hj-hairline)',
        }}
      >
        <p
          className="m-0"
          style={{
            color: 'var(--hj-ink-3)',
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          Ready to work with {reader.first_name}?
        </p>
        <button
          type="button"
          onClick={() => (isAuthenticated ? setShowInviteDialog(true) : setShowLogin(true))}
          className="inline-flex items-center gap-2"
          style={{
            padding: '12px 24px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 48,
          }}
        >
          <CalendarDays size={16} strokeWidth={2} />
          Book a session
        </button>
      </div>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => {
          setShowLogin(false);
          setShowInviteDialog(true);
        }}
      />

      <InviteReaderDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        reader={reader}
        onInviteSent={() => {
          setShowInviteDialog(false);
          toast({
            title: 'Invitation sent',
            description: `We'll let you know when ${reader.first_name} responds.`,
          });
        }}
      />
    </main>
  );
};

const RateCard: React.FC<{ label: string; rate: number }> = ({ label, rate }) => (
  <div
    className="text-center"
    style={{
      padding: '18px 16px',
      borderRadius: 'var(--hj-r-md)',
      background: 'var(--hj-surface-1)',
      border: '1px solid var(--hj-hairline)',
    }}
  >
    <div
      className="tabular"
      style={{
        fontSize: 28,
        fontWeight: 600,
        color: 'var(--hj-ink)',
        fontFamily: 'var(--hj-font-mono)',
        lineHeight: 1,
        letterSpacing: '-0.02em',
      }}
    >
      ${rate.toFixed(2)}
    </div>
    <div
      style={{
        marginTop: 8,
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--hj-ink-2)',
      }}
    >
      {label}
    </div>
    <div className="mono-label" style={{ marginTop: 4, fontSize: 9 }}>
      per 15 min
    </div>
  </div>
);

export default ReaderProfilePage;